"use client";

import { useEffect, useState } from "react";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { MentionTextarea } from "@/components/community/MentionTextarea";
import { UserLevelBadge, AdminAuthorBadge } from "@/components/community/UserLevelBadge";
import { marketStatusLabel } from "@/components/community/PostRow";
import type { CommunityPostDetail, CommunityCommentOut } from "@/types";

// 마이그 050 + 사용자 결정 2026-05-05: 가입한 무료 회원부터 댓글 작성 가능
const WRITE_TIERS = ["free_auth", "paid", "franchise"];
const METRIC_LABEL: Record<string, string> = {
  revenue: "매출",
  qty: "판매량",
  rating: "재방문율",
  other: "지표",
};
// 마이그 105 — 중고장터 라벨/색상
const MARKET_STATUS_COLOR: Record<string, string> = {
  selling: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  reserved: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  sold: "bg-gray-500/10 text-gray-400 border-gray-500/30",
};
const TRADE_METHOD_LABEL: Record<string, string> = { direct: "직거래", delivery: "택배", both: "직거래+택배" };
const CONDITION_LABEL: Record<string, string> = { new: "새 상품", like_new: "거의 새것", used: "사용감 있음" };
const MARKET_STATUS_OPTIONS: ("selling" | "reserved" | "sold")[] = ["selling", "reserved", "sold"];
function fmtMarketPrice(p: { price_type?: string | null; price?: number | null }): string {
  if (p.price_type === "free") return "무료나눔";
  if (p.price_type === "negotiable") return "가격협의";
  if (p.price != null) return `${p.price.toLocaleString()}원`;
  return "가격협의";
}

// @닉네임 멘션 텍스트를 강조 표시 (간단 highlight, 클릭 X)
const MENTION_RE = /(@[\w가-힣][\w가-힣]{0,29})/g;
function renderWithMentions(text: string): React.ReactNode[] {
  const parts = text.split(MENTION_RE);
  return parts.map((p, i) =>
    MENTION_RE.test(p) ? (
      <span key={i} className="text-primary font-semibold">{p}</span>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

export default function CommunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  // 비로그인도 글 보기 OK — 댓글·좋아요 시 로그인 redirect
  const { token, isLoggedIn: rawIsLoggedIn, user } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  // hydration 전엔 false 로 가정 (UI 분기는 hydration 후에만 정확)
  const isLoggedIn = hasHydrated && rawIsLoggedIn;
  const canWrite = !!user && WRITE_TIERS.includes(user.tier);

  const [post, setPost] = useState<CommunityPostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [replyTo, setReplyTo] = useState<number | null>(null);
  const [replyInput, setReplyInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // 수정 모드 (사용자 피드백 2026-05-02)
  const [editingPost, setEditingPost] = useState(false);
  const [editPostContent, setEditPostContent] = useState("");
  const [editPostTitle, setEditPostTitle] = useState("");
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  const load = async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const r = await axios.get<CommunityPostDetail>(`/api/community/posts/${id}`, { headers });
      setPost(r.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasHydrated) return;     // hydration 전엔 fetch 보류 (token 미확정)
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token, hasHydrated]);

  const toggleLike = async () => {
    if (!token) {
      router.push("/login");
      return;
    }
    if (!post) return;
    try {
      const r = await axios.post(
        `/api/community/posts/${post.id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPost({ ...post, liked: r.data.liked, like_count: r.data.like_count });
    } catch {
      // noop
    }
  };

  const submitComment = async (content: string, parentId: number | null) => {
    if (!token || !post || !content.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        `/api/community/posts/${post.id}/comments`,
        { content: content.trim(), parent_comment_id: parentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (parentId) {
        setReplyTo(null);
        setReplyInput("");
      } else {
        setCommentInput("");
      }
      load();
    } catch {
      // noop
    } finally {
      setSubmitting(false);
    }
  };

  const deletePost = async () => {
    if (!token || !post) return;
    if (!confirm("정말 삭제하시겠어요?")) return;
    await axios.delete(`/api/community/posts/${post.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    router.push("/community");
  };

  const openEditPost = () => {
    if (!post) return;
    setEditPostContent(post.content);
    setEditPostTitle(post.title ?? "");
    setEditingPost(true);
  };

  const submitEditPost = async () => {
    if (!token || !post) return;
    const c = editPostContent.trim();
    if (!c) return;
    await axios.patch(`/api/community/posts/${post.id}`,
      { content: c, title: editPostTitle.trim() || null },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setEditingPost(false);
    load();
  };

  const submitEditComment = async (id: number) => {
    if (!token) return;
    const c = editCommentContent.trim();
    if (!c) return;
    await axios.patch(`/api/community/comments/${id}`,
      { content: c },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    setEditingComment(null);
    load();
  };

  const deleteComment = async (id: number) => {
    if (!token || !confirm("댓글을 삭제할까요?")) return;
    await axios.delete(`/api/community/comments/${id}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    load();
  };

  const reportPost = async () => {
    if (!token || !post) return;
    if (!confirm("이 게시물을 신고하시겠어요?")) return;
    try {
      await axios.post(
        `/api/community/posts/${post.id}/report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("신고가 접수됐어요");
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      const status = err?.response?.status;
      // 409 = 이미 신고함 (Slice 8 fix — CommunityReport UNIQUE). 안내 후 종료
      if (status === 409) {
        alert("이미 신고한 게시물입니다.");
        return;
      }
      alert("신고 실패: " + (err?.response?.data?.detail ?? "잠시 후 다시 시도해주세요"));
    }
  };

  // R3-33 찬반 (poll) 투표
  const castVote = async (side: "pro" | "con") => {
    if (!token) {
      router.push("/login");
      return;
    }
    if (!post) return;
    try {
      const r = await axios.post<{
        pro_count: number; con_count: number; total: number;
        my_side: "pro" | "con" | null; is_closed: boolean;
      }>(
        `/api/community/posts/${post.id}/vote`,
        { side },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPost({
        ...post,
        poll: post.poll ? {
          ...post.poll,
          pro_count: r.data.pro_count,
          con_count: r.data.con_count,
          total: r.data.total,
          my_side: r.data.my_side,
        } : null,
      });
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      alert(detail ?? "투표 실패");
    }
  };

  const cancelVote = async () => {
    if (!token || !post) return;
    try {
      const r = await axios.delete<{
        pro_count: number; con_count: number; total: number;
        my_side: null; is_closed: boolean;
      }>(
        `/api/community/posts/${post.id}/vote`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPost({
        ...post,
        poll: post.poll ? {
          ...post.poll,
          pro_count: r.data.pro_count,
          con_count: r.data.con_count,
          total: r.data.total,
          my_side: null,
        } : null,
      });
    } catch { /* noop */ }
  };

  // 마이그 105 — 장터 거래 상태 변경 (작성자 단독). 낙관적 업데이트 — 즉시 반영 후 실패 시 롤백.
  const changeMarketStatus = async (newStatus: "selling" | "reserved" | "sold") => {
    if (!token || !post || post.market_status === newStatus) return;
    const prev = post.market_status;
    setPost({ ...post, market_status: newStatus });
    try {
      await axios.patch(
        `/api/community/posts/${post.id}/market-status`,
        { market_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (e) {
      setPost((p) => (p ? { ...p, market_status: prev } : p));
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      alert(detail ?? "상태 변경 실패");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin text-4xl">🌀</div>
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-text-sub">게시물을 찾을 수 없어요.</p>
        <Link href="/community" className="mt-4 inline-block text-primary text-sm">
          ← 커뮤니티로
        </Link>
      </div>
    );
  }

  const isOwner = user?.id === post.user_id;
  const parents = post.comments.filter((c) => !c.parent_comment_id);
  const repliesByParent = post.comments.reduce<Record<number, CommunityCommentOut[]>>((acc, c) => {
    if (c.parent_comment_id) (acc[c.parent_comment_id] ||= []).push(c);
    return acc;
  }, {});

  return (
    <div className="pb-24">
      {/* 헤더 */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => router.back()} className="text-text-sub text-xl" aria-label="뒤로">
            ←
          </button>
          <h1 className="text-sm font-bold text-text">{post.post_type === "market" ? "🛒 장터" : "후기"}</h1>
          {isOwner ? (
            <div className="flex items-center gap-2">
              <button
                onClick={openEditPost}
                className="text-xs text-primary font-semibold"
              >
                수정
              </button>
              <button onClick={deletePost} className="text-xs text-red-500 font-semibold">
                삭제
              </button>
            </div>
          ) : isLoggedIn ? (
            <button onClick={reportPost} className="text-xs text-text-sub" aria-label="신고">
              🚨
            </button>
          ) : (
            <span className="w-6"></span>
          )}
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* 본문 */}
        <section className="card">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {post.post_type === "item_tagged" && post.keyword_id && post.keyword ? (
              <Link href={`/trend/${post.keyword_id}`}>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                  🏷 #{post.keyword}
                </span>
              </Link>
            ) : post.post_type === "poll" ? (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-400/10 text-yellow-400">
                ⚖️ 찬반
              </span>
            ) : post.post_type === "market" ? (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                post.trade_type === "buy" ? "bg-blue-500/10 text-blue-500" : "bg-rose-500/10 text-rose-500"
              }`}>
                🛒 {post.trade_type === "buy" ? "삽니다" : "팝니다"}
              </span>
            ) : (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-bg-elevated text-text-sub">
                💭 자유글
              </span>
            )}
            <span className="text-[12px] font-semibold text-text">@{post.author_name || "익명"}</span>
            {post.author_is_admin ? (
              <AdminAuthorBadge />
            ) : (
              <UserLevelBadge level={post.author_level} label={post.author_label} emoji={post.author_emoji} />
            )}
            <span className="text-2xs text-text-sub">
              · {new Date(post.created_at).toLocaleString("ko-KR", {
                month: "numeric",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {/* 2026-06-02 — 지역·카테고리 인라인 (큰 칩 → 메타 줄 자연 흡수) */}
            {post.region && (
              <span className="text-2xs text-text-sub">· 📍 {post.region}</span>
            )}
            {post.category && (
              <span className="text-2xs text-text-sub">· {post.category}</span>
            )}
          </div>
          {editingPost && (post.post_type === "free" || post.post_type === "poll" || post.post_type === "market") && (
            <input
              value={editPostTitle}
              onChange={(e) => setEditPostTitle(e.target.value)}
              maxLength={200}
              placeholder={post.post_type === "market" ? "품목명" : "제목 (선택)"}
              className="w-full bg-bg-secondary text-base font-black text-text mb-2 p-2 rounded-lg border border-primary/40 outline-none focus:border-primary"
            />
          )}
          {!editingPost && (post.post_type === "free" || post.post_type === "poll" || post.post_type === "market") && post.title && (
            <h2 className="text-base font-black text-text mb-2 leading-tight">{post.title}</h2>
          )}

          {/* R3-33 S4-D 지역/카테고리 — 2026-06-02 상단 메타 줄 인라인으로 이동. 큰 칩 제거. */}

          {/* 마이그 105 — 중고장터 정보 카드 */}
          {post.post_type === "market" && (
            <div className="mb-3 rounded-xl border border-border bg-bg-secondary p-3">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-xl font-black ${post.market_status === "sold" ? "text-text-sub line-through" : "text-text"}`}>
                  {fmtMarketPrice(post)}
                </span>
                {post.market_status && (
                  <span className={`text-2xs font-bold px-2 py-0.5 rounded border ${MARKET_STATUS_COLOR[post.market_status]}`}>
                    {marketStatusLabel(post.market_status, post.trade_type)}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {post.market_category && (
                  <span className="text-2xs text-text-sub bg-bg-elevated px-2 py-0.5 rounded">{post.market_category}</span>
                )}
                {post.trade_method && (
                  <span className="text-2xs text-text-sub bg-bg-elevated px-2 py-0.5 rounded">{TRADE_METHOD_LABEL[post.trade_method]}</span>
                )}
                {post.item_condition && (
                  <span className="text-2xs text-text-sub bg-bg-elevated px-2 py-0.5 rounded">{CONDITION_LABEL[post.item_condition]}</span>
                )}
              </div>

              {/* 작성자 — 거래 상태 토글 / 비작성자 — 거래 문의 */}
              {isOwner ? (
                <div className="mt-3">
                  <p className="text-2xs text-text-sub mb-1.5">거래 상태 변경</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {MARKET_STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => changeMarketStatus(s)}
                        className={`text-2xs py-1.5 rounded-lg border font-semibold transition-colors ${
                          post.market_status === s
                            ? "bg-primary text-bg border-primary"
                            : "border-border text-text-sub hover:text-text"
                        }`}
                      >
                        {marketStatusLabel(s, post.trade_type)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : isLoggedIn ? (
                <Link
                  href={`/mypage/dm?with=${post.user_id}&market_post=${post.id}`}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-primary text-bg font-bold text-sm hover:opacity-90 transition-opacity"
                >
                  💬 거래 문의
                </Link>
              ) : (
                <Link
                  href="/login"
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-border text-text-sub font-semibold text-sm"
                >
                  로그인하고 거래 문의
                </Link>
              )}

              {/* 면책 고지 */}
              <p className="mt-3 text-2xs text-text-sub leading-relaxed border-t border-border pt-2">
                ⚠️ 거래는 회원 간 직접 진행됩니다. 트렌드레인은 통신판매중개자로서 거래에 관여하지
                않으며 책임지지 않습니다. 선입금 요구·시세 대비 과도하게 저렴한 매물은 사기일 수
                있으니 직거래·안전결제를 권장합니다.
              </p>
            </div>
          )}

          {editingPost ? (
            <>
              <textarea
                value={editPostContent}
                onChange={(e) => setEditPostContent(e.target.value)}
                rows={6}
                maxLength={2000}
                className="w-full bg-bg-secondary text-sm p-2 rounded-lg border border-primary/40 outline-none focus:border-primary"
              />
              <div className="flex justify-between items-center mt-1.5">
                <span className="text-2xs text-text-sub">{editPostContent.length} / 2000</span>
                <div className="flex gap-3">
                  <button onClick={() => setEditingPost(false)} className="text-xs text-text-sub">
                    취소
                  </button>
                  <button
                    onClick={submitEditPost}
                    disabled={!editPostContent.trim()}
                    className="text-xs text-primary font-bold disabled:opacity-30"
                  >
                    저장
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-sm text-text leading-relaxed whitespace-pre-wrap">
              {renderWithMentions(post.content)}
            </p>
          )}

          {/* 마이그 039 — 첨부 이미지 그리드 */}
          {post.images && post.images.length > 0 && (
            <div className={`mt-3 grid gap-1.5 ${post.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
              {post.images.map((url) => (
                // eslint-disable-next-line @next/next/no-img-element
                <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={url}
                    alt=""
                    className={`w-full ${post.images!.length === 1 ? "max-h-[480px]" : "aspect-square"} object-cover rounded-lg border border-border hover:opacity-90 transition-opacity`}
                  />
                </a>
              ))}
            </div>
          )}

          {/* R3-33 찬반 (poll) UI */}
          {post.post_type === "poll" && post.poll && (
            <div className="mt-3 bg-yellow-400/5 border border-yellow-400/30 rounded-lg p-3">
              {(() => {
                const { pro_count, con_count, total, my_side, is_closed, closes_at } = post.poll;
                const proPct = total > 0 ? Math.round((pro_count / total) * 100) : 0;
                const conPct = total > 0 ? 100 - proPct : 0;
                const closesText = closes_at
                  ? new Date(closes_at).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })
                  : null;
                return (
                  <>
                    <div className="flex items-center justify-between text-2xs text-text-sub mb-2">
                      <span>📊 {total}명 참여</span>
                      {is_closed ? (
                        <span className="text-red-400 font-bold">🔒 마감됨</span>
                      ) : closesText ? (
                        <span>⏱ {closesText} 마감</span>
                      ) : null}
                    </div>
                    {/* 찬성 bar */}
                    <div className="space-y-2">
                      <button
                        onClick={() => !is_closed && castVote("pro")}
                        disabled={is_closed || !isLoggedIn}
                        className={`w-full text-left rounded-lg overflow-hidden transition-all ${
                          my_side === "pro" ? "ring-2 ring-primary" : ""
                        } ${is_closed ? "cursor-default" : "hover:ring-2 hover:ring-primary/50 cursor-pointer"}`}
                      >
                        <div className="relative">
                          <div className="absolute inset-0 bg-primary/20" style={{ width: `${proPct}%` }} />
                          <div className="relative flex items-center justify-between px-3 py-2.5">
                            <span className="text-sm font-bold text-text">
                              👍 찬성 {my_side === "pro" && <span className="text-2xs text-primary ml-1">✓ 내 표</span>}
                            </span>
                            <span className="text-sm font-bold text-text">
                              {proPct}% <span className="text-2xs text-text-sub">({pro_count})</span>
                            </span>
                          </div>
                        </div>
                      </button>
                      {/* 반대 bar */}
                      <button
                        onClick={() => !is_closed && castVote("con")}
                        disabled={is_closed || !isLoggedIn}
                        className={`w-full text-left rounded-lg overflow-hidden transition-all ${
                          my_side === "con" ? "ring-2 ring-red-400" : ""
                        } ${is_closed ? "cursor-default" : "hover:ring-2 hover:ring-red-400/50 cursor-pointer"}`}
                      >
                        <div className="relative">
                          <div className="absolute inset-0 bg-red-400/20" style={{ width: `${conPct}%` }} />
                          <div className="relative flex items-center justify-between px-3 py-2.5">
                            <span className="text-sm font-bold text-text">
                              👎 반대 {my_side === "con" && <span className="text-2xs text-red-400 ml-1">✓ 내 표</span>}
                            </span>
                            <span className="text-sm font-bold text-text">
                              {conPct}% <span className="text-2xs text-text-sub">({con_count})</span>
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>
                    {my_side && !is_closed && (
                      <button onClick={cancelVote} className="text-2xs text-text-sub underline mt-2">
                        투표 취소
                      </button>
                    )}
                    {!isLoggedIn && (
                      <p className="text-2xs text-text-sub mt-2 text-center">
                        <Link href="/login" className="text-primary">로그인</Link> 후 투표 가능
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* R3-33 S4-D — 매장 인사이트 카드 */}
          {post.insight && (
            <div className="mt-3 bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-2xs text-primary font-bold">📊 매장 인사이트</p>
                  <p className="text-sm text-text font-bold mt-0.5">
                    {post.insight.menu_name ?? "메뉴"}
                  </p>
                  <p className="text-2xs text-text-sub mt-0.5">
                    {METRIC_LABEL[post.insight.metric_type] ?? post.insight.metric_type}: {post.insight.metric_before} → {post.insight.metric_after}
                  </p>
                </div>
                {post.insight.change_pct !== null && (
                  <p className={`text-2xl font-black shrink-0 ${
                    post.insight.change_pct >= 0 ? "text-primary" : "text-red-400"
                  }`}>
                    {post.insight.change_pct >= 0 ? "↑" : "↓"} {Math.abs(post.insight.change_pct)}%
                  </p>
                )}
              </div>
              <p className="text-2xs text-text-sub mt-2 italic">
                ※ 작성자 매장 데이터 기준 (참고용)
              </p>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-border flex items-center gap-5">
            <button
              onClick={toggleLike}
              className={`text-sm font-semibold transition-colors ${
                post.liked ? "text-primary" : "text-text-sub hover:text-text"
              }`}
            >
              {post.liked ? "❤️" : "🤍"} {post.like_count}
            </button>
            <span className="text-sm text-text-sub">💬 {post.comment_count}</span>
            <span className="text-sm text-text-sub ml-auto">👁 {post.view_count ?? 0}</span>
          </div>
        </section>

        {/* 댓글 */}
        <section className="card">
          <h2 className="text-sm font-bold text-text mb-3">
            댓글 {post.comment_count}
          </h2>

          {parents.length === 0 ? (
            <p className="text-xs text-text-sub text-center py-4">
              첫 댓글을 남겨보세요
            </p>
          ) : (
            <div className="space-y-4">
              {parents.map((c) => (
                <div key={c.id}>
                  {editingComment === c.id ? (
                    <div className="bg-bg-secondary border border-primary/40 rounded-lg p-2">
                      <textarea
                        value={editCommentContent}
                        onChange={(e) => setEditCommentContent(e.target.value)}
                        rows={3}
                        maxLength={500}
                        className="w-full bg-bg text-xs p-2 rounded border border-border outline-none focus:border-primary/60"
                      />
                      <div className="flex justify-end gap-3 mt-1.5">
                        <button onClick={() => setEditingComment(null)} className="text-2xs text-text-sub">
                          취소
                        </button>
                        <button
                          onClick={() => submitEditComment(c.id)}
                          disabled={!editCommentContent.trim()}
                          className="text-2xs text-primary font-bold disabled:opacity-30"
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  ) : (
                    <CommentRow
                      comment={c}
                      onReply={canWrite ? () => setReplyTo(c.id) : undefined}
                      onChanged={load}
                      isOwner={!!user && user.id === c.user_id}
                      onEdit={() => {
                        setEditCommentContent(c.content);
                        setEditingComment(c.id);
                      }}
                      onDelete={() => deleteComment(c.id)}
                    />
                  )}
                  {(repliesByParent[c.id] || []).map((r) => (
                    <div key={r.id} className="ml-6 mt-2 pl-3 border-l border-border">
                      {editingComment === r.id ? (
                        <div className="bg-bg-secondary border border-primary/40 rounded-lg p-2">
                          <textarea
                            value={editCommentContent}
                            onChange={(e) => setEditCommentContent(e.target.value)}
                            rows={3}
                            maxLength={500}
                            className="w-full bg-bg text-xs p-2 rounded border border-border outline-none focus:border-primary/60"
                          />
                          <div className="flex justify-end gap-3 mt-1.5">
                            <button onClick={() => setEditingComment(null)} className="text-2xs text-text-sub">
                              취소
                            </button>
                            <button
                              onClick={() => submitEditComment(r.id)}
                              disabled={!editCommentContent.trim()}
                              className="text-2xs text-primary font-bold disabled:opacity-30"
                            >
                              저장
                            </button>
                          </div>
                        </div>
                      ) : (
                        <CommentRow
                          comment={r}
                          onChanged={load}
                          isOwner={!!user && user.id === r.user_id}
                          onEdit={() => {
                            setEditCommentContent(r.content);
                            setEditingComment(r.id);
                          }}
                          onDelete={() => deleteComment(r.id)}
                        />
                      )}
                    </div>
                  ))}
                  {replyTo === c.id && (
                    <div className="ml-6 mt-2">
                      <MentionTextarea
                        value={replyInput}
                        onChange={setReplyInput}
                        contextPostId={post.id}
                        placeholder="답글 (@닉네임 호출 가능)"
                        rows={2}
                        maxLength={500}
                        className="w-full bg-bg-secondary text-xs p-2 rounded-lg border border-border outline-none focus:border-primary/50"
                      />
                      <div className="flex justify-end gap-3 mt-1">
                        <button
                          onClick={() => {
                            setReplyTo(null);
                            setReplyInput("");
                          }}
                          className="text-2xs text-text-sub"
                        >
                          취소
                        </button>
                        <button
                          onClick={() => submitComment(replyInput, c.id)}
                          disabled={!replyInput.trim() || submitting}
                          className="text-2xs text-primary font-bold disabled:opacity-30"
                        >
                          등록
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* 댓글 입력 */}
          <div className="mt-4 pt-3 border-t border-border">
            {canWrite ? (
              <>
                <MentionTextarea
                  value={commentInput}
                  onChange={setCommentInput}
                  contextPostId={post.id}
                  placeholder="댓글 작성 (@닉네임 호출 가능)"
                  rows={2}
                  maxLength={500}
                  className="w-full bg-bg-secondary text-xs p-2 rounded-lg border border-border outline-none focus:border-primary/50"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-2xs text-text-sub">{commentInput.length} / 500</span>
                  <button
                    onClick={() => submitComment(commentInput, null)}
                    disabled={!commentInput.trim() || submitting}
                    className="text-xs text-primary font-bold disabled:opacity-30"
                  >
                    댓글 등록
                  </button>
                </div>
              </>
            ) : isLoggedIn ? (
              <p className="text-2xs text-text-sub text-center">
                🔒 댓글은 회원 가입 후 작성할 수 있어요 ·{" "}
                <Link href="/mypage/subscribe" className="text-primary">
                  구독하기
                </Link>
              </p>
            ) : (
              <p className="text-2xs text-text-sub text-center">
                <Link href="/login" className="text-primary">
                  로그인
                </Link>
                하면 댓글을 작성할 수 있어요
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  onReply,
  onChanged,
  isOwner,
  onEdit,
  onDelete,
}: {
  comment: CommunityCommentOut;
  onReply?: () => void;
  onChanged?: () => void;
  isOwner?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const { token, isLoggedIn } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const toggleLike = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `/api/community/comments/${comment.id}/like`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };
  const isBest = comment.best_rank && comment.best_rank <= 3;
  return (
    <div className={isBest ? "p-2 -mx-2 rounded-lg bg-yellow-500/5 border border-yellow-500/20" : ""}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        {isBest && (
          <span className="text-2xs font-bold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-300 border border-yellow-500/40">
            🏅 베스트 {comment.best_rank}
          </span>
        )}
        <span className="text-2xs font-semibold text-text">@{comment.author_name || "익명"}</span>
        {comment.author_is_admin ? (
          <AdminAuthorBadge />
        ) : (
          <UserLevelBadge level={comment.author_level} label={comment.author_label} emoji={comment.author_emoji} />
        )}
        <span className="text-2xs text-text-sub">
          · {new Date(comment.created_at).toLocaleString("ko-KR", {
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      </div>
      <p className="text-xs text-text whitespace-pre-wrap leading-relaxed">{renderWithMentions(comment.content)}</p>
      <div className="flex items-center gap-3 mt-1.5">
        <button
          onClick={toggleLike}
          disabled={!isLoggedIn || busy}
          className={`text-2xs flex items-center gap-1 transition-colors ${
            comment.liked ? "text-red-400 font-semibold" : "text-text-sub hover:text-text"
          } disabled:opacity-50`}
        >
          {comment.liked ? "❤️" : "🤍"} {(comment.like_count ?? 0) > 0 ? comment.like_count : "좋아요"}
        </button>
        {onReply && (
          <button onClick={onReply} className="text-2xs text-text-sub hover:text-text">
            답글
          </button>
        )}
        {isOwner && onEdit && (
          <button onClick={onEdit} className="text-2xs text-primary hover:opacity-80 ml-auto">
            수정
          </button>
        )}
        {isOwner && onDelete && (
          <button onClick={onDelete} className="text-2xs text-red-400 hover:opacity-80">
            삭제
          </button>
        )}
      </div>
    </div>
  );
}
